import { useEffect, useRef } from 'react';
import { useTheme } from '../../ThemeContext';

/**
 * AuthFigure — the decorative 3D figure on the signed-out screens (Auth.dc.html's aside).
 * A canvas port of the design's SkeletonView in pose mode: floor grid, XYZ axes and a
 * stick figure raising its arms. It is illustration, not data, so it is aria-hidden and
 * takes no input. Colours come from the EGDL tokens; reduced motion gets a still frame.
 */
const BONES = [[0, 1], [1, 2], [2, 3], [2, 4], [4, 5], [5, 6], [2, 7], [7, 8], [8, 9], [0, 10], [10, 11], [11, 12], [0, 13], [13, 14], [14, 15]];
const CAM = { yaw: 0.5, pitch: 0.18, zoom: 1 };

function arm(sh, side, th, ph) {
    const d = [side * Math.sin(th), -Math.cos(th), 0];
    const e = sh.map((c, i) => c + d[i] * 0.28);
    const d2 = [d[0] * Math.cos(ph), d[1] * Math.cos(ph), Math.sin(ph)];
    return [e, e.map((c, i) => c + d2[i] * 0.25)];
}

function pose(t) {
    const a = (Math.sin(t * 1.3) + 1) / 2;
    const b = (Math.sin(t * 1.3 + 2) + 1) / 2;
    const P = [[0, 1, 0], [0, 1.25, 0.02], [0, 1.48, 0], [0, 1.68, 0.02], [-0.19, 1.45, 0], 0, 0, [0.19, 1.45, 0], 0, 0,
        [-0.1, 0.97, 0], [-0.11, 0.53, 0.04], [-0.11, 0.08, 0], [0.1, 0.97, 0], [0.11, 0.53, 0.04], [0.11, 0.08, 0]];
    [P[5], P[6]] = arm(P[4], -1, 0.15 + b * 0.9, 0.5);
    [P[8], P[9]] = arm(P[7], 1, a * 2.1, 0.3 + 0.4 * a);
    return P;
}

/** What the figure is drawn from, for its marking ("16 joints / 15 bones"). */
export const FIGURE_JOINTS = pose(0).length;
export const FIGURE_BONES = BONES.length;

function readColors(el) {
    const cs = getComputedStyle(el);
    const c = (n) => cs.getPropertyValue('--egdl-' + n).trim();
    return {
        grid: c('border'), bone: c('text-tertiary'), joint: c('text-primary'), hand: c('accent'),
        ax: c('axis-x'), ay: c('axis-y'), az: c('axis-z'),
    };
}

function draw(canvas, col, t) {
    const W = canvas.clientWidth;
    const H = canvas.clientHeight;
    if (!W || !H) return;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
        canvas.width = Math.round(W * dpr);
        canvas.height = Math.round(H * dpr);
    }
    const g = canvas.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, W, H);
    const { yaw, pitch, zoom } = CAM;
    const pr = ([x, y0, z]) => {
        const y = y0 - 0.9;
        const x1 = x * Math.cos(yaw) + z * Math.sin(yaw);
        const z1 = -x * Math.sin(yaw) + z * Math.cos(yaw);
        const y1 = y * Math.cos(pitch) - z1 * Math.sin(pitch);
        const z2 = y * Math.sin(pitch) + z1 * Math.cos(pitch);
        const s = (Math.min(W, H) * 1.3) / (3.4 / zoom - z2);
        return [W / 2 + x1 * s, H / 2 - y1 * s];
    };
    const line = (a, b, color, width) => {
        const p = pr(a);
        const q = pr(b);
        g.strokeStyle = color;
        g.lineWidth = width;
        g.beginPath();
        g.moveTo(p[0], p[1]);
        g.lineTo(q[0], q[1]);
        g.stroke();
    };
    for (let k = -4; k <= 4; k++) {
        line([k / 4, 0, -1], [k / 4, 0, 1], col.grid, 1);
        line([-1, 0, k / 4], [1, 0, k / 4], col.grid, 1);
    }
    line([0, 0, 0], [0.3, 0, 0], col.ax, 2);
    line([0, 0, 0], [0, 0.3, 0], col.ay, 2);
    line([0, 0, 0], [0, 0, 0.3], col.az, 2);
    const P = pose(t);
    BONES.forEach(([a, b]) => line(P[a], P[b], col.bone, 3));
    P.forEach((p, i) => {
        const q = pr(p);
        const s = i === 3 ? 14 : 8;
        g.fillStyle = i >= 7 && i <= 9 ? col.hand : col.joint;
        g.fillRect(q[0] - s / 2, q[1] - s / 2, s, s);
    });
}

export default function AuthFigure() {
    const ref = useRef(null);
    const { theme } = useTheme();

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return undefined;
        const col = readColors(canvas);
        const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const t0 = performance.now();
        let raf = 0;
        const now = () => (still ? 2 : (performance.now() - t0) / 1000);
        const loop = () => {
            draw(canvas, col, now());
            if (!still) raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        const ro = new ResizeObserver(() => draw(canvas, col, now()));
        ro.observe(canvas);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [theme]);

    return <canvas ref={ref} className="au-aside__canvas" aria-hidden="true" />;
}
